import { getSalesCountryCopy, type SalesCountryCopyKey } from "@/config/sales-country-copy";
import nbTaxonomyCopy from "@/lib/category/nb-taxonomy-copy.json";

type SaleorCategory = {
	slug?: string | null;
	name?: string | null;
	translation?: { name?: string | null } | null;
};

const RESEARCH_MATERIALS = "Research materials";

/**
 * Storefront taxonomy is intentionally small. These assignments preserve the
 * current catalog intent while Saleor's live catalog remains under its
 * internal default category.
 */
const CURATED_PRODUCT_CATEGORIES: Record<string, readonly string[]> = {
	"5-amino-1mq": ["Weight Management"],
	"ahk-cu": ["Skin"],
	"aod-9604": ["Weight Management"],
	b12: ["B12"],
	"bac-water-10ml": ["Laboratory Supplies"],
	"bpc-157": ["Recovery", "Skin", "Anti-Aging"],
	cagrilintide: ["Weight Management"],
	"cjc-1295": ["Growth Hormone", "Recovery", "Anti-Aging"],
	"cjc-1295-dac": ["Growth Hormone"],
	"cjc-ipa-blend": ["Growth Hormone"],
	dsip: ["Sleep Research"],
	epithalon: ["Anti-Aging"],
	"ghk-cu": ["Recovery", "Skin", "Anti-Aging"],
	"glow-stack": ["Recovery", "Skin", "Anti-Aging"],
	ipamorelin: ["Growth Hormone"],
	"kisspeptin-10": ["Kisspeptin"],
	"klow-stack": ["Recovery"],
	"melanotan-1": ["Skin", "Melanocortins"],
	"mots-c": ["Anti-Aging", "Mitochondrial Research"],
	"mt-2": ["Skin", "Melanocortins"],
	"nad-plus": ["Anti-Aging", "Mitochondrial Research"],
	pinealon: ["Cognition"],
	"pt-141": ["Melanocortins"],
	retatrutide: ["Weight Management"],
	selank: ["Cognition"],
	semaglutide: ["Weight Management"],
	semax: ["Cognition"],
	"snap-8": ["Skin"],
	sermorelin: ["Growth Hormone"],
	"ss-31": ["Anti-Aging", "Mitochondrial Research"],
	survodutide: ["Weight Management"],
	"tb-500": ["Recovery", "Anti-Aging"],
	"tb500-bpc157-blend": ["Recovery", "Anti-Aging"],
	tesamorelin: ["Weight Management", "Growth Hormone", "Anti-Aging"],
	"thymosin-alpha-1": ["Recovery", "Immunology"],
	tirzepatid: ["Weight Management"],
	vip: ["Immunology"],
	kpv: ["Immunology"],
};

const LOCALIZED_CATEGORY_LABELS: Record<string, Record<string, string>> = {
	"Weight Management": {
		de: "Gewichtsmanagement",
		en: "Weight management",
		es: "Control de peso",
		tr: "Kilo yönetimi",
		nb: "Vektstyring",
	},
	"Growth Hormone": {
		de: "Wachstumshormon",
		en: "Growth hormone",
		es: "Hormona del crecimiento",
		tr: "Büyüme hormonu",
		nb: "Veksthormon",
	},
	Recovery: { de: "Regeneration", en: "Recovery", es: "Recuperación", tr: "İyileşme", nb: "Regenerering" },
	Skin: { de: "Haut", en: "Skin", es: "Piel", tr: "Cilt", nb: "Hud" },
	"Anti-Aging": {
		de: "Alterungsforschung",
		en: "Ageing research",
		es: "Investigación del envejecimiento",
		tr: "Yaşlanma araştırması",
		nb: "Aldringsforskning",
	},
	Cognition: { de: "Kognition", en: "Cognition", es: "Cognición", tr: "Biliş", nb: "Kognisjon" },
	"Laboratory Supplies": {
		de: "Labormaterialien",
		en: "Laboratory supplies",
		es: "Material de laboratorio",
		tr: "Laboratuvar malzemeleri",
		nb: nbTaxonomyCopy["category.Laboratory Supplies"],
	},
	Immunology: { de: "Immunologie", en: "Immunology", es: "Inmunología", tr: "İmmünoloji", nb: "Immunologi" },
	Melanocortins: {
		de: "Melanocortine",
		en: "Melanocortins",
		es: "Melanocortinas",
		tr: "Melanokortinler",
		nb: nbTaxonomyCopy["category.Melanocortins"],
	},
	"Sleep Research": {
		de: "Schlafforschung",
		en: "Sleep research",
		es: "Investigación del sueño",
		tr: "Uyku araştırması",
		nb: nbTaxonomyCopy["category.Sleep Research"],
	},
	"Mitochondrial Research": {
		de: "Mitochondrienforschung",
		en: "Mitochondrial research",
		es: "Investigación mitocondrial",
		tr: "Mitokondriyal araştırma",
		nb: nbTaxonomyCopy["category.Mitochondrial Research"],
	},
	[RESEARCH_MATERIALS]: {
		de: "Forschungsmaterialien",
		en: RESEARCH_MATERIALS,
		es: "Materiales de investigación",
		tr: "Araştırma materyalleri",
		nb: nbTaxonomyCopy["category.Research materials"],
	},
};

function nonEmptyCategories(categories: readonly string[] | undefined): string[] {
	return [...new Set(categories?.map((category) => category.trim()).filter(Boolean) ?? [])];
}

function isDefaultCategory(category: SaleorCategory | null | undefined): boolean {
	if (!category) return false;
	return [category.slug, category.name, category.translation?.name].some(
		(value) =>
			value
				?.trim()
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, "") === "defaultcategory",
	);
}

/** Resolves product badge categories without inventing research metadata or scores. */
export function getProductCategories(
	slug: string,
	metadataCategories?: readonly string[],
	category?: SaleorCategory | null,
): string[] {
	const curated = CURATED_PRODUCT_CATEGORIES[slug];
	if (curated) return [...curated];

	const metadata = nonEmptyCategories(metadataCategories);
	if (metadata.length) return metadata;

	if (category && !isDefaultCategory(category)) {
		const saleorCategory =
			category.translation?.name?.trim() || category.name?.trim() || category.slug?.trim();
		if (saleorCategory) return [saleorCategory];
	}

	return [RESEARCH_MATERIALS];
}

/** Product slugs carrying a curated category tag, in catalog order. */
export function getCuratedProductSlugs(category: string): string[] {
	return Object.entries(CURATED_PRODUCT_CATEGORIES)
		.filter(([, categories]) => categories.includes(category))
		.map(([slug]) => slug);
}

export function translateProductCategory(value: string, locale: string): string {
	const labels = LOCALIZED_CATEGORY_LABELS[value];
	if (labels && !labels[locale]) {
		const translated = getSalesCountryCopy(locale, `category.${value}` as SalesCountryCopyKey);
		if (translated !== undefined) return translated;
	}
	return labels?.[locale] ?? labels?.en ?? value;
}
